import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { uploadFile } from '../helper/firebase';
import ReactConfetti from 'react-confetti';
import { logout } from '../helper/auth';
import AddressFields, { emptyAddress, addressToSubfields } from './AddressFields';

export default function OnboardingForm({onComplete,firebase_uid, setDj, dj}) {
  // Tracks whether user has worked with us before: null (not chosen), "yes", or "no"
  const [hasWorkedBefore, setHasWorkedBefore] = useState(null);

  // Form fields for the "No" scenario
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [stageName, setStageName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [textMessageConsent, setTextMessageConsent] = useState(false);
  const [isWorkAuthorized, setIsWorkAuthorized] = useState(null);
  const [userImages, setUserImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);

  // Home vs. Shipping Address (USPS-verified, decomposed fields)
  const [home, setHome] = useState(emptyAddress);
  const [shipping, setShipping] = useState(emptyAddress);
  const [sameAddress, setSameAddress] = useState(false); // if true, shipping address = home address
  const [homeVerified, setHomeVerified] = useState(false);
  const [shippingVerified, setShippingVerified] = useState(false);

  // Refs so Submit can trigger USPS verification imperatively on each address field.
  const homeRef = useRef(null);
  const shippingRef = useRef(null);
  // DOM refs to the address containers, so when verification halts the submit (a USPS
  // suggestion or error is shown inline) we can smooth-scroll the DJ up to it — otherwise
  // they're left at the submit button, unaware, and may re-click thinking it's stuck.
  const homeScrollRef = useRef(null);
  const shippingScrollRef = useRef(null);

  // Social Media
  const [instagram, setInstagram] = useState("");
  const [instagramFollowers, setInstagramFollowers] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [tiktokFollowers, setTiktokFollowers] = useState("");
  const [facebook, setFacebook] = useState("");
  const [facebookFollowers, setFacebookFollowers] = useState("");
  const [twitter, setTwitter] = useState("");
  const [twitterFollowers, setTwitterFollowers] = useState("");
  const [twitch, setTwitch] = useState("");
  const [twitchFollowers, setTwitchFollowers] = useState("");

  // Website
  const [hasWebsite, setHasWebsite] = useState(false);
  const [website, setWebsite] = useState("");

  // Organization Connection
  const [hasOrgConnection, setHasOrgConnection] = useState(false);
  const [orgConnection, setOrgConnection] = useState("");

  // Form fields for the "Yes" scenario
  const [existingContact, setExistingContact] = useState(""); // phone or email
  const [errorMessage, setErrorMessage] = useState("");
  // Masked login email of the account the looked-up DJ is already tied to (server sends it masked).
  const [linkedLoginEmail, setLinkedLoginEmail] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [isVerifyingUser, setIsVerifyingUser] = useState(false);
  const [isUpdatingExistingUser, setIsUpdatingExistingUser] = useState(false);

  // Keep shipping mirrored to home (incl. verified state) while "Same as home" is checked.
  useEffect(() => {
    if (sameAddress) {
      setShipping(home);
      setShippingVerified(homeVerified);
    }
  }, [sameAddress, home, homeVerified]);

  // Revoke object-URL image previews when the form unmounts (on submit-success via
  // onComplete, or navigation) so the full-size blobs don't stay pinned for the tab's
  // life. A ref tracks the latest images so the unmount-only effect sees them all.
  const userImagesRef = useRef(userImages);
  useEffect(() => {
    userImagesRef.current = userImages;
  }, [userImages]);
  useEffect(() => () => {
    userImagesRef.current.forEach(img => img.preview && URL.revokeObjectURL(img.preview));
  }, []);

  // Add this state for drag and drop functionality
  const [isDragging, setIsDragging] = useState(false);

  // Handle image selection
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || e.dataTransfer.files);

    if (userImages.length + files.length > 3) {
      alert("You can only upload up to 3 images");
      return;
    }

    // Per-file type + size guard (mirrors EDayCheckIn). The <input accept> is only a
    // hint; reject non-images and anything over 15MB with clear feedback.
    const MAX_BYTES = 15 * 1024 * 1024;
    const validFiles = [];
    const rejected = [];
    for (const file of files) {
      if (!file.type || !file.type.startsWith('image/')) {
        rejected.push(`${file.name || 'file'} (not an image)`);
      } else if (file.size > MAX_BYTES) {
        rejected.push(`${file.name} (over 15MB)`);
      } else {
        validFiles.push(file);
      }
    }
    if (rejected.length > 0) {
      alert(`These files were skipped:\n${rejected.join('\n')}`);
    }
    if (validFiles.length === 0) return;

    // Create preview for each image
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setUserImages(prev => [...prev, ...newImages]);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e);
      e.dataTransfer.clearData();
    }
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    setUserImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Handle submit for the "No" scenario
  const handleNewUserSubmit = async (e) => {
    // Also re-invoked (without an event) by AddressFields when the DJ confirms a soft/partial match.
    e?.preventDefault?.();
    setUploadProgress(true);

    try {
      // Verify addresses on submit (shipping exempt when it mirrors home). verify() returns the
      // standardized (canonical) address when confirmed, or false when it shows a correction
      // suggestion/error inline — halt (and smooth-scroll to the failing field so the DJ sees it,
      // rather than re-clicking Submit) so they can resolve and submit again. We persist the
      // returned standardized address so QuickBase stores the canonical form, not the DJ's raw typing.
      let homeAddr = home;
      if (!homeVerified) {
        const r = homeRef.current && await homeRef.current.verify();
        if (!r) {
          homeScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return; // finally resets uploadProgress
        }
        homeAddr = r;
      }
      let shippingSrc;
      if (sameAddress) {
        shippingSrc = homeAddr;
      } else {
        shippingSrc = shipping;
        if (!shippingVerified) {
          const r = shippingRef.current && await shippingRef.current.verify();
          if (!r) {
            shippingScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return; // finally resets uploadProgress
          }
          shippingSrc = r;
        }
      }
      if (!instagram.trim()) {
        throw new Error("Instagram handle is required. Please enter 'n/a' if you don't have one.");
      }

      if (!stageName.trim()) {
        throw new Error("DJ name is required.");
      }

      if (userImages.length === 0) {
        throw new Error("Please upload at least one photo of yourself performing as a DJ.");
      }
      //include rid for existing user if it exists      
      let existingUser = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/getDJs?phone=${phone}&email=${email}&rid=${foundUser?.rid? foundUser.rid : ""}`
      );
      console.log("Existing user data:", existingUser);
      if (existingUser.data.length > 0) {
        setShowDuplicateError(true);
        setUploadProgress(false);
        return;
      }

      // Upload images to Firebase SEQUENTIALLY (not Promise.all). Concurrent uploads
      // of full-res photos on a mobile uplink starve each other and blow Firebase's
      // retry window — the root cause of storage/retry-limit-exceeded on the 2nd/3rd
      // image. We also cache each success onto the image entry so that if a later
      // upload fails, a retry doesn't re-upload the ones that already succeeded.
      const uploadResults = [];
      for (let index = 0; index < userImages.length; index++) {
        const image = userImages[index];
        if (image.uploaded) {
          uploadResults.push(image.uploaded);
          continue;
        }
        const uploadResult = await uploadFile(image.file, index);
        // Persist the success so a subsequent failure + retry skips this image.
        setUserImages(prev => {
          const next = [...prev];
          if (next[index]) next[index] = { ...next[index], uploaded: uploadResult };
          return next;
        });
        uploadResults.push(uploadResult);
      }

      // Verify that we have the correct number of unique uploads
      const uniqueFileNames = new Set(uploadResults.map(result => result.fileName));
      if (uniqueFileNames.size !== uploadResults.length) {
        throw new Error("Duplicate file uploads detected. Please try again.");
      }

      // Log all upload results for verification
      console.log("All image upload results:", uploadResults);
      
      // Create DJ object with form data and image URLs
      const djData = {
        rid: foundUser?.rid ? foundUser.rid : null,
        first_name: firstName,
        last_name: lastName,
        stage_name: stageName,
        email_address: email,
        phone_number: phone,
        text_message_consent: textMessageConsent,
        is_work_authorized: isWorkAuthorized,
        // Subfields are the source of truth; backend rolls them up into the compiled fields.
        ...addressToSubfields(homeAddr, 'home'),
        ...addressToSubfields(shippingSrc, 'shipping'),
        instagram_handle: instagram,
        instagram_followers: instagramFollowers,
        tiktok_handle: tiktok,
        tiktok_followers: tiktokFollowers,
        facebook_handle: facebook,
        facebook_followers: facebookFollowers,
        twitter_handle: twitter,
        twitter_followers: twitterFollowers,
        twitch_handle: twitch,
        twitch_followers: twitchFollowers,
        website: hasWebsite ? website : null,
        has_org_connection: hasOrgConnection ? "Yes" : "No",
        org_connection_details: hasOrgConnection ? orgConnection : null,
        firebase_uid: firebase_uid,
      };
      if (uploadResults.length > 0) {
        uploadResults.forEach((result, index) => {
          djData[`profile_img_url_${index + 1}`] = result.url;
        });
      }

      console.log("Submitting DJ info with verified images:", djData);
      let res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/createDJ`, djData);
      let dj = res.data;
      if (dj) {
        setShowConfirmation(true);
        setShowConfetti(true);
        // Wait for 10 seconds before completing
        setTimeout(() => {
          setShowConfirmation(false);
          setShowConfetti(false);
          setDj(dj);
          onComplete(dj);
        }, 10000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(error.message || "There was an error submitting your form. Please try again.");
    } finally {
      setUploadProgress(false);
    }
  };

  // Handle check for the "Yes" scenario
  const handleCheckExisting = async () => {
    setErrorMessage(""); // Clear any previous error
    setLinkedLoginEmail("");
    setFoundUser(null); // Clear any previous found user
    setIsVerifyingUser(false);
    
    console.log("Checking if user is in the system with:", existingContact);
    const cleanPhone = existingContact.replace(/\D/g, '');
    //Regexes to valid email and phone
    const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/
    const phoneRegex = /^\d{10}$/;
    let res;

    //validate email and phone
    try {
      if (emailRegex.test(existingContact)) {
        console.log("Valid Email", existingContact);
        // res = await fetch(`http://localhost:80/checkUserExist?email=${existingContact}`);
        res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/checkUserExist?email=${existingContact}`);
        
      } else if (phoneRegex.test(cleanPhone)) {
        console.log("Valid Phone", cleanPhone);
        //res = await fetch(`http://localhost:80/checkUserExist?phone=${cleanPhone}`);
        res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/checkUserExist?phone=${cleanPhone}`);
      } else {
        //invalid input
        setErrorMessage("Please enter a valid email or phone number");
        return;
      }
      let data = res.data;
      if (data) {
        setFoundUser(data);
        setDj(data);
        setIsVerifyingUser(true);
        console.log("User exists in the system:", data);
      } else {
        setErrorMessage("We couldn't find you. Please try the email or phone number you may have previously used when working with us.");

      }

    } catch(error){
      console.error(error);
      if (error.response?.status === 409 && error.response.data?.masked_login_email) {
        setLinkedLoginEmail(error.response.data.masked_login_email);
      }
      setErrorMessage("There was an error submitting the form");
    }
  };

  // Add this helper function to determine input type
  const isEmail = (value) => {
    const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
    return emailRegex.test(value);
  };

  const isPhoneNumber = (value) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(value.replace(/\D/g, ''));
  };

  const getErrorMessage = () => {
    if (!existingContact) return null;

    if (linkedLoginEmail) {
      return `We already have a DJ with this info, but they are associated with this login email: ${linkedLoginEmail}. If this is you, sign out and sign back in using that email to access your account.`;
    }

    if (isEmail(existingContact)) {
      return "Hmm, we can't find a DJ that matches that email address. Please try again using another email address or use the phone number we've been texting you at instead.";
    }
    
    if (isPhoneNumber(existingContact)) {
      return "Hmm, we can't find a DJ with that phone number. Please try again using the phone number we've been texting you at or try an email address instead.";
    }

    return "Hmm, we can't find a DJ with that phone number or email. Please try again using the phone number we've been texting you at or try an email address instead.";
  };

  // Helper function to mask email
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    const maskedUsername = username.charAt(0) + '*'.repeat(username.length - 2) + username.charAt(username.length - 1);
    return `${maskedUsername}@${domain}`;
  };

  // Helper function to mask phone number
  const maskPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return `(***) ***-${cleaned.slice(-4)}`;
  };

  // Phone number validation and formatting
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    let formatted = cleaned;
    if (cleaned.length >= 3) {
      formatted = `(${cleaned.slice(0, 3)}`;
      if (cleaned.length >= 6) {
        formatted += `) ${cleaned.slice(3, 6)}`;
        if (cleaned.length >= 10) {
          formatted += `-${cleaned.slice(6, 10)}`;
        } else if (cleaned.length > 6) {
          formatted += `-${cleaned.slice(6)}`;
        }
      } else if (cleaned.length > 3) {
        formatted += `) ${cleaned.slice(3)}`;
      }
    }
    return formatted;
  };

  const validatePhoneNumber = (value) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(value.replace(/\D/g, ''));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setPhone(formatted);

    if (value.replace(/\D/g, '').length === 0) {
      setPhoneError("");
    } else if (!validatePhoneNumber(value)) {
      setPhoneError("Please enter a valid 10-digit phone number");
    } else {
      setPhoneError("");
    }
  };

  // Handle user verification response
  const handleUserVerification = (isUser) => {
    setIsUpdatingExistingUser(isUser);
    if (isUser) {
      // Pre-populate the form with found user data, except for address fields
      setFirstName(foundUser.first_name || "");
      setLastName(foundUser.last_name || "");
      setStageName(foundUser.stage_name || "");
      setEmail(foundUser.email_address || "");
      setPhone(foundUser.phone_number || "");
      // Address fields are intentionally left empty (must be entered & USPS-verified).
      setHome(emptyAddress);
      setShipping(emptyAddress);
      setHomeVerified(false);
      setShippingVerified(false);
      setSameAddress(false);
      setInstagram(foundUser.instagram_handle || "");
      setInstagramFollowers(foundUser.instagram_followers || "");
      setTiktok(foundUser.tiktok_handle || "");
      setTiktokFollowers(foundUser.tiktok_followers || "");
      setFacebook(foundUser.facebook_handle || "");
      setFacebookFollowers(foundUser.facebook_followers || "");
      setTwitter(foundUser.twitter_handle || "");
      setTwitterFollowers(foundUser.twitter_followers || "");
    } else {
      // Not them: forget the looked-up record so the new DJ is created, not saved over it by rid.
      setFoundUser(null);
      setDj(null);
    }
    //update DJ
    console.log(foundUser);
    // Switch to the registration form view
    setHasWorkedBefore("no");
  };

  // Dynamic heading logic
  const headingText =
    hasWorkedBefore === null
      ? "Welcome!"
      : hasWorkedBefore === "yes"
      ? "Let's see if we can find you!"
      : isUpdatingExistingUser
      ? "Welcome back!"
      : "Welcome! We are happy to have you spin with us.";

  // Dynamic subheading logic
  const subheadingText =
    hasWorkedBefore === null ? (
      <>
        Did you apply or work with  {" "}
        <span className="italic font-bold text-[#3966C3]">
          DJs at The Polls
        </span>{" "}
        in the 2024 election?
      </>
    ) : hasWorkedBefore === "yes" ? (
      "Please enter your phone number or email to check if you’re already in our system."
    ) : isUpdatingExistingUser ? (
      "Please confirm or edit your contact information below."
    ) : (
      "First, we need some information from you..."
    );

  // Form description text
  const formDescriptionText = isUpdatingExistingUser ? (
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <p className="text-blue-800">
        We've pre-filled the form with your existing information. Please:
      </p>
      <ul className="list-disc list-inside mt-2 text-blue-700 space-y-1">
        <li>Review each field carefully</li>
        <li>Update any information that has changed</li>
        <li>Fill in any missing information</li>
      </ul>
    </div>
  ) : null;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const exampleImages = [
    '/DJ-img1.png',
    '/DJ-img2.png',
    '/DJ-img3.png',
    '/DJ-img4.png'
  ];

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === exampleImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const previousImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? exampleImages.length - 1 : prevIndex - 1
    );
  };

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Error Popup Component
  const DuplicateErrorPopup = () => (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="duplicate-error-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="sm:flex sm:items-start">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 
                className="text-lg font-semibold leading-6 text-gray-900" 
                id="duplicate-error-title"
              >
                Duplicate Information Detected
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  We already have a DJ registered with that phone number or email address. Please try again using different contact information.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => setShowDuplicateError(false)}
              className="mt-3 inline-flex justify-center w-full px-4 py-2 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Fixed logout header */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center h-16">
            <button
              onClick={async () => {
                try {
                  await logout();
                  // window.location.reload();
                } catch (error) {
                  console.error('Error logging out:', error);
                  alert('Error logging out. Please try again.');
                }
              }}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-[#3966C3] hover:bg-[#2850a7] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3966C3] transition-all duration-200 shadow-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign-out
            </button>
          </div>
        </div>
      </div>
      {/* Main content with padding to account for fixed header */}
      <div className="max-w-3xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg">
      {showDuplicateError && <DuplicateErrorPopup />}
      <h1 className="mb-8 text-4xl font-bold text-primary text-left">
        {headingText}
      </h1>
      <p className="text-2xl text-gray-800 text-left">{subheadingText}</p>

      {/* If user hasn't chosen, show the "Have you worked with us before?" prompt */}
      {hasWorkedBefore === null && (
        <>
          <div className="mt-6 flex justify-center space-x-4">
            <button
              onClick={() => setHasWorkedBefore("yes")}
              className="px-6 py-3 bg-[#3966C3] text-white rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Yes
            </button>
            <button
              onClick={() => setHasWorkedBefore("no")}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              No
            </button>
          </div>
        </>
      )}

      {/* If user says "No" --> show the larger form */}
      {hasWorkedBefore === "no" && (
        <form onSubmit={handleNewUserSubmit} className="mt-6 space-y-4">
          {formDescriptionText}
          {/* First / Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="lastName">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* DJ Name */}
          <div>
            <label className="block mb-1 text-gray-700" htmlFor="stageName">
              DJ Name <span className="text-red-500">*</span>
            </label>
            <input
              id="stageName"
              type="text"
              required
              value={stageName}
              onChange={(e) => setStageName(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block mb-1 text-gray-700" htmlFor="email">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Cell Phone Number */}
          <div>
            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-md">
              <p className="text-sm text-blue-800">
                This phone number will be used to process your payment after completing your set(s). Please ensure this is your personal number and avoid using VOIP numbers as they may pose security risks. (For example, Google Voice is a VOIP number).
              </p>
            </div>
            <label className="block mb-1 text-gray-700" htmlFor="phone">
              Cell Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={phone}
              onChange={handlePhoneChange}
              required
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                phoneError ? 'border-red-500' : 'border-gray-300'
              }`}
              autoComplete="off"
            />
            {phoneError && (
              <p className="mt-1 text-sm text-red-500">{phoneError}</p>
            )}
          </div>

          {/* Text Message Consent */}
          <div className="mt-4">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="textMessageConsent"
                  name="textMessageConsent"
                  type="checkbox"
                  checked={textMessageConsent}
                  onChange={(e) => setTextMessageConsent(e.target.checked)}
                  required
                  className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
              </div>
              <label htmlFor="textMessageConsent" className="ml-3 text-sm">
                <span className="text-gray-700">
                  By checking this box, I agree to receive text messages from DJs at the Polls related to the election. Message frequency varies. Message & data rates may apply. <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">
                Note: Texting is our primary form of communication with DJs. If you do not opt in, we may not be able to hire you.
              </p>
            </div>
          </div>

          {/* Work Authorization */}
          <div>
            <label className="block mb-1 text-gray-700">
              Are you authorized to work in the US? <span className="text-red-500">*</span>
            </label>
            <div className="mt-2 space-x-6">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="workAuthorization"
                  value="yes"
                  checked={isWorkAuthorized === true}
                  onChange={() => setIsWorkAuthorized(true)}
                  required
                  className="form-radio h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="ml-2">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="workAuthorization"
                  value="no"
                  checked={isWorkAuthorized === false}
                  onChange={() => setIsWorkAuthorized(false)}
                  required
                  className="form-radio h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="ml-2">No</span>
              </label>
            </div>
          </div>

          {/* Home Address */}
          <div ref={homeScrollRef}>
            <AddressFields
              ref={homeRef}
              label="Home Address"
              value={home}
              onChange={setHome}
              required
              verified={homeVerified}
              onVerifiedChange={setHomeVerified}
              onConfirmSave={handleNewUserSubmit}
            />
          </div>

          {/* Same as home address */}
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={sameAddress}
              onChange={(e) => {
                setSameAddress(e.target.checked);
                if (e.target.checked) {
                  setShipping(home);
                  setShippingVerified(homeVerified);
                }
              }}
              className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            Same as home address
          </label>

          {/* Shipping Address */}
          {!sameAddress && (
            <div ref={shippingScrollRef}>
              <AddressFields
                ref={shippingRef}
                label="Shipping Address"
                value={shipping}
                onChange={setShipping}
                required
                verified={shippingVerified}
                onVerifiedChange={setShippingVerified}
                onConfirmSave={handleNewUserSubmit}
              />
            </div>
          )}

          {/* Social Media */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Instagram Handle */}
            <div className="space-y-2">
              <label className="block mb-1 text-gray-700" htmlFor="instagram">
                Instagram Handle <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">@</span>
                </div>
                <input
                  type="text"
                  name="instagram"
                  id="instagram"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  className="focus:ring-primary focus:border-primary block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  required
                />
              </div>
              <p className="text-xs text-gray-500">Enter 'n/a' if you don't have an Instagram account and fill out the other social media/website sections.</p>
            </div>

            {/* Instagram Followers */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="instagram_followers">
                Instagram Followers <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="instagram_followers"
                id="instagram_followers"
                value={instagramFollowers}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0) {
                    setInstagramFollowers(value);
                  }
                }}
                step="1"
                min="0"
                className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
              />
            </div>

            {/* TikTok Handle */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="tiktok">
                TikTok Handle <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">@</span>
                </div>
                <input
                  type="text"
                  name="tiktok_handle"
                  id="tiktok"
                  value={tiktok}
                  onChange={(e) => setTiktok(e.target.value)}
                  className="block w-full pl-8 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <p className="text-xs text-gray-500">Enter 'n/a' if you don't have a TikTok account.</p>
            </div>

            {/* TikTok Followers */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="tiktok_followers">
                TikTok Followers <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="tiktok_followers"
                id="tiktok_followers"
                value={tiktokFollowers}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0) {
                    setTiktokFollowers(value);
                  }
                }}
                step="1"
                min="0"
                className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
              />
            </div>

            {/* Facebook Handle */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="facebook">
                Facebook Handle
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">@</span>
                </div>
                <input
                  type="text"
                  name="facebook_handle"
                  id="facebook"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  className="block w-full pl-8 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            {/* Facebook Followers */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="facebook_followers">
                Facebook Followers
              </label>
              <input
                type="number"
                name="facebook_followers"
                id="facebook_followers"
                value={facebookFollowers}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0) {
                    setFacebookFollowers(value);
                  }
                }}
                step="1"
                min="0"
                className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Twitter (X) Handle */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="twitter">
                Twitter (X) Handle
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">@</span>
                </div>
                <input
                  type="text"
                  name="twitter_handle"
                  id="twitter"
                  value={twitter}
                  onChange={(e) => setTwitter(e.target.value)}
                  className="block w-full pl-8 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            {/* Twitter (X) Followers */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="twitter_followers">
                Twitter (X) Followers
              </label>
              <input
                type="number"
                name="twitter_followers"
                id="twitter_followers"
                value={twitterFollowers}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0) {
                    setTwitterFollowers(value);
                  }
                }}
                step="1"
                min="0"
                className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Twitch Handle */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="twitch">
                Twitch Handle
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">@</span>
                </div>
                <input
                  type="text"
                  name="twitch_handle"
                  id="twitch"
                  value={twitch || ""}
                  onChange={(e) => setTwitch(e.target.value)}
                  className="block w-full pl-8 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            {/* Twitch Followers */}
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="twitch_followers">
                Twitch Followers
              </label>
              <input
                type="number"
                name="twitch_followers"
                id="twitch_followers"
                value={twitchFollowers}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0) {
                    setTwitchFollowers(value);
                  }
                }}
                step="1"
                min="0"
                className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Your Photos <span className="text-red-500">*</span></h3>
              <p className="text-sm text-gray-600 mb-4">Add up to 3 photos of yourself performing as a DJ (at least 1 required)</p>

              {/* Image Requirements and Example Photos */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Requirements List */}
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Photo Requirements:</h4>
                    <ul className="text-sm text-blue-700 space-y-2">
                      <li className="flex items-start">
                        <div className="w-5 h-5 flex-shrink-0 mr-2">
                          <svg className="w-full h-full text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        Your face is clearly visible
                      </li>
                      <li className="flex items-start">
                        <div className="w-5 h-5 flex-shrink-0 mr-2">
                          <svg className="w-full h-full text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        Your DJ equipment is clearly visible (at least your deck and speakers)
                      </li>
                      <li className="flex items-start">
                        <div className="w-5 h-5 flex-shrink-0 mr-2">
                          <svg className="w-full h-full text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        Gig photos are perfect!
                      </li>
                      <li className="flex items-start">
                        <div className="w-5 h-5 flex-shrink-0 mr-2">
                          <svg className="w-full h-full text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        High quality photo (well-lit, clear, and in focus)
                      </li>
                      <li className="flex items-start">
                        <div className="w-5 h-5 flex-shrink-0 mr-2">
                          <svg className="w-full h-full text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        Horizontal photo is preferred
                      </li>
                    </ul>
                  </div>

                  {/* Example Photos Carousel */}
                  <div className="relative">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Example Photos:</h4>
                    <div className="relative rounded-lg overflow-hidden aspect-[4/3]">
                      <img
                        src={exampleImages[currentImageIndex]}
                        alt={`Example DJ ${currentImageIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {/* Navigation Arrows */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          previousImage();
                        }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                        aria-label="Previous image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          nextImage();
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                        aria-label="Next image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      {/* Image Counter */}
                      <div className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                        {currentImageIndex + 1} / {exampleImages.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Image Preview Grid */}
              {userImages.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-4">
                  {userImages.map((image, index) => (
                    <div key={index} className="relative flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
                      {/* Image Preview */}
                      <div className="aspect-w-16 aspect-h-9 bg-gray-50">
                        <img
                          src={image.preview}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      
                      {/* Image Info and Remove Button */}
                      <div className="p-3 flex items-center justify-between bg-white">
                        <span className="text-sm text-gray-600">
                          Photo {index + 1} of 3
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0111 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              {userImages.length < 3 && (
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    multiple={userImages.length === 0}
                    disabled={uploadProgress}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer
                      ${uploadProgress ? 'bg-gray-100 border-gray-300' : isDragging ? 'bg-blue-50 border-blue-500' : 'bg-white border-[#3966C3] hover:bg-blue-50'}
                      transition-colors duration-200 ease-in-out`}
                    onDragEnter={handleDragIn}
                    onDragLeave={handleDragOut}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className={`w-8 h-8 mb-2 ${isDragging ? 'text-blue-500' : 'text-[#3966C3]'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      <p className="text-sm text-gray-600">
                        {uploadProgress ? 'Uploading...' : isDragging ? 'Drop your images here' : `Click or drag to upload ${userImages.length === 0 ? 'photos' : 'another photo'}`}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {userImages.length === 0 ? 'You can select multiple photos' : `${3 - userImages.length} spots remaining`}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Upload Progress */}
              {uploadProgress && (
                <div className="mt-2">
                  <div className="flex items-center justify-center text-sm text-blue-600">
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading your photos...
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Website */}
          <div className="space-y-2">
            <p className="text-gray-700">Do you have a website?</p>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="hasWebsite"
                  value="yes"
                  checked={hasWebsite === true}
                  onChange={() => setHasWebsite(true)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span>Yes</span>
              </label>
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="hasWebsite"
                  value="no"
                  checked={hasWebsite === false}
                  onChange={() => {
                    setHasWebsite(false);
                    setWebsite(""); // Clear website when toggled off
                  }}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span>No</span>
              </label>
            </div>
            {hasWebsite && (
              <div>
                <label
                  className="block mb-1 text-gray-700"
                  htmlFor="websiteLink"
                >
                  Website Link <span className="text-red-500">*</span>
                </label>
                <input
                  id="websiteLink"
                  type="url"
                  required={hasWebsite}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}
          </div>

          {/* Organization Connection */}
          <div className="space-y-2 mt-4">
            <p className="text-gray-700">Do you have a strong connection to a Church, School, or other organization that you think might be a local polling place?</p>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="hasOrgConnection"
                  value="yes"
                  checked={hasOrgConnection === true}
                  onChange={() => setHasOrgConnection(true)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span>Yes</span>
              </label>
              <label className="flex items-center space-x-1">
                <input
                  type="radio"
                  name="hasOrgConnection"
                  value="no"
                  checked={hasOrgConnection === false}
                  onChange={() => {
                    setHasOrgConnection(false);
                    setOrgConnection(""); // Clear connection details when toggled off
                  }}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span>No</span>
              </label>
            </div>
            {hasOrgConnection && (
              <div>
                <label
                  className="block mb-1 text-gray-700"
                  htmlFor="orgConnection"
                >
                  If yes, please list it here, and tell us how you're connected <span className="text-xs text-gray-500">(for example: I'm a Deacon at XXX Church, or I'm a coach at XXX High School)</span>
                </label>
                <textarea
                  id="orgConnection"
                  rows="3"
                  required={hasOrgConnection}
                  value={orgConnection}
                  onChange={(e) => setOrgConnection(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Describe your connection here..."
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={uploadProgress}
              className={`w-full py-3 text-white rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                uploadProgress
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-accent'
              }`}
            >
              {uploadProgress ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      )}

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
          gravity={0.08}
          initialVelocityY={10}
          confettiSource={{
            x: 0,
            y: 0,
            w: window.innerWidth,
            h: 0
          }}
          tweenDuration={5000}
          // onConfettiComplete={() => setShowConfetti(false)}
        />
        </div>
      )}

      {/* Confirmation Popup */}
      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"></div>
          <div className="relative bg-white rounded-2xl p-8 max-w-md mx-4 text-center transform transition-all duration-300 scale-100 shadow-2xl border-2 border-primary">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Thanks for creating your DJs at the Polls account!
            </h3>
            <p className="text-lg text-gray-600 mb-2">
              You will now be taken to DJs Hub home page.
            </p>
            <p className="text-primary font-medium">
              Next stop: applying to work for a specific election!
            </p>
          </div>
        </div>
      )}

      {/* If user says "Yes" --> show smaller form to check in the system */}
      {hasWorkedBefore === "yes" && (
        <div className="mt-6">
          {/* <p className="text-gray-600 mb-4">
            Please enter either your phone number or email and click "Check" to
            see if you're already in our system.
          </p> */}
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-gray-700" htmlFor="existingContact">
                Phone or Email
              </label>
              <input
                id="existingContact"
                type="text"
                required
                value={existingContact}
                onChange={(e) => {
                  setExistingContact(e.target.value);
                  setErrorMessage(""); // Clear error when user types
                  setLinkedLoginEmail("");
                }}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button
              onClick={handleCheckExisting}
              disabled={isVerifyingUser}
              className={`w-full py-3 bg-primary text-white rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                isVerifyingUser ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent'
              }`}
            >
              Check If You Are In The System
            </button>

            {/* Error Message */}
            {errorMessage && !isVerifyingUser && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 font-medium mb-2">
                  {getErrorMessage()}
                </p>
                <p className="text-red-600 text-sm mb-3">
                  Otherwise,{" "}
                  <button
                    type="button"
                    onClick={() => setHasWorkedBefore("no")}
                    className="text-red-600 hover:text-red-800 underline font-medium"
                  >
                    create a new account here.
                  </button>
                </p>
                <p className="text-sm text-gray-600">
                  If you're stuck, email{" "}
                  <a href="mailto:djs@djsatthepolls.org" className="text-blue-600 hover:underline">
                    djs@djsatthepolls.org
                  </a>
                  {" "}for help.
                </p>
              </div>
            )}

            {/* User Verification */}
            {isVerifyingUser && foundUser && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Looks like we may have found you!</h3>
                <p className="text-sm text-blue-600 mb-4">Please double-check and confirm this is you. If this is you, and you need to update your email or phone number, select "Yes, that's me" to make edits on the next screen.</p>
                <div className="space-y-2 text-sm text-gray-700 mb-4">
                  <p><span className="font-medium">DJ Name:</span> {foundUser.stage_name}</p>
                  <p><span className="font-medium">Name:</span> {foundUser.first_name} {foundUser.last_name}</p>
                  <p><span className="font-medium">Email:</span> {maskEmail(foundUser.email_address)}</p>
                  <p><span className="font-medium">Phone:</span> {maskPhoneNumber(foundUser.phone_number)}</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleUserVerification(true)}
                    className="flex-1 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Yes, that's me
                  </button>
                  <button
                    onClick={() => handleUserVerification(false)}
                    className="flex-1 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    No, that's not me
                  </button>
                </div>
              </div>
            )}

            {/* New Link: "Not in our system? Create a new account" */}
            <p className="text-sm text-gray-600 text-center mt-2">
              Not in our system?{" "}
              <button
                onClick={() => setHasWorkedBefore("no")}
                className="text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Create a new account
              </button>
            </p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}